// =============================================================================
// FILE: packages/renderer/src/components/branding/RcddBadge.tsx
// PURPOSE: Professional "RCDD Certified" credential badge.
//          Rendered in two variants:
//            'inline' — compact pill for the TopBar header
//            'card'   — full credential widget for the Dashboard profile section
//
// Visibility rule: contractor | designer tier + verified status only.
// =============================================================================

import type { JSX } from 'react';
import { ShieldCheck } from 'lucide-react';

export interface RcddBadgeProps {
  bicsiId: string;
  tier: 'contractor' | 'designer';
  /** 'inline' → small TopBar pill. 'card' → full credential card. Default: 'inline' */
  variant?: 'inline' | 'card';
}

// ── Format bicsiId for display ────────────────────────────────────────────────
function formatId(raw: string): string {
  if (raw === 'MASTER-ADMIN') return 'MASTER';
  if (raw === 'dev-preview')  return 'DEV';
  // Normalise: strip any existing # prefix, uppercase
  return raw.replace(/^#/, '').toUpperCase();
}

// ── Tier label ─────────────────────────────────────────────────────────────────
const TIER_LABEL: Record<RcddBadgeProps['tier'], string> = {
  contractor: 'Contractor',
  designer:   'Enterprise',
};

// ─── Inline pill (TopBar) ─────────────────────────────────────────────────────

function InlinePill({ bicsiId, tier }: { bicsiId: string; tier: RcddBadgeProps['tier'] }): JSX.Element {
  const id = formatId(bicsiId);
  return (
    /* Gradient-border wrapper */
    <div
      data-testid="rcdd-badge-inline"
      title={`RCDD Certified · BICSI ID #${id} · ${TIER_LABEL[tier]} Tier`}
      style={{
        padding: '1px',
        borderRadius: '20px',
        background: 'linear-gradient(130deg, rgba(37,181,168,0.90) 0%, rgba(99,179,237,0.70) 50%, rgba(37,181,168,0.90) 100%)',
        boxShadow: '0 0 10px rgba(37,181,168,0.22), 0 0 20px rgba(59,130,246,0.08)',
        flexShrink: 0,
      }}
    >
      <div
        style={{
          display:        'flex',
          alignItems:     'center',
          gap:            '5px',
          padding:        '2px 10px 2px 8px',
          borderRadius:   '19px',
          background:     'linear-gradient(160deg, rgb(13,28,54) 0%, rgb(10,22,46) 100%)',
          whiteSpace:     'nowrap',
        }}
      >
        <ShieldCheck
          style={{ width: 11, height: 11, color: 'rgba(94,234,212,0.95)', flexShrink: 0 }}
          strokeWidth={2.25}
        />
        <span
          style={{
            fontSize:      10,
            fontWeight:    800,
            letterSpacing: '0.20em',
            textTransform: 'uppercase',
            color:         '#8FF7EA',
            textShadow:    '0 0 6px rgba(94,234,212,0.6)',
          }}
        >
          RCDD
        </span>
        <span
          style={{
            width: 1, height: 10,
            background: 'rgba(94,234,212,0.25)',
            flexShrink: 0,
          }}
        />
        <span
          style={{
            fontSize:      9,
            fontFamily:    'IBM Plex Mono, monospace',
            letterSpacing: '0.10em',
            color:         'rgba(148,200,210,0.80)',
          }}
        >
          #{id}
        </span>
      </div>
    </div>
  );
}

// ─── Full credential card (Dashboard) ────────────────────────────────────────

function CredentialCard({ bicsiId, tier }: { bicsiId: string; tier: RcddBadgeProps['tier'] }): JSX.Element {
  const id        = formatId(bicsiId);
  const tierLabel = TIER_LABEL[tier];
  const isElite   = tier === 'designer';

  return (
    /* Outer gradient-border frame */
    <div
      data-testid="rcdd-badge-card"
      style={{
        padding:      '1px',
        borderRadius: '12px',
        background:   isElite
          ? 'linear-gradient(135deg, rgba(252,211,77,0.70) 0%, rgba(37,181,168,0.60) 45%, rgba(252,211,77,0.70) 100%)'
          : 'linear-gradient(135deg, rgba(37,181,168,0.80) 0%, rgba(99,179,237,0.60) 50%, rgba(37,181,168,0.80) 100%)',
        boxShadow: isElite
          ? '0 0 24px rgba(252,211,77,0.14), 0 0 48px rgba(37,181,168,0.08), 0 4px 16px rgba(0,0,0,0.45)'
          : '0 0 24px rgba(37,181,168,0.16), 0 0 48px rgba(59,130,246,0.06), 0 4px 16px rgba(0,0,0,0.45)',
        flexShrink: 0,
      }}
    >
      {/* Inner card */}
      <div
        style={{
          borderRadius: '11px',
          background:   'linear-gradient(155deg, rgba(15,32,62,0.98) 0%, rgba(10,22,46,0.99) 100%)',
          padding:      '14px 18px',
          position:     'relative',
          overflow:     'hidden',
        }}
      >
        {/* Subtle grain overlay for metallic texture */}
        <div
          aria-hidden="true"
          style={{
            position:         'absolute',
            inset:             0,
            borderRadius:     '11px',
            backgroundImage:  'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'200\' height=\'200\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.85\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3CfeColorMatrix type=\'saturate\' values=\'0\'/%3E%3C/filter%3E%3Crect width=\'200\' height=\'200\' filter=\'url(%23n)\' opacity=\'0.03\'/%3E%3C/svg%3E")',
            backgroundSize:   '200px',
            pointerEvents:    'none',
          }}
        />

        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
          <div>
            <div
              style={{
                fontSize:      8,
                fontWeight:    700,
                letterSpacing: '0.30em',
                textTransform: 'uppercase',
                color:         'rgba(148,172,210,0.55)',
                marginBottom:  2,
              }}
            >
              BICSI Certified
            </div>
            <div
              style={{
                fontSize:      8,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color:         isElite ? 'rgba(252,211,77,0.55)' : 'rgba(94,234,212,0.50)',
              }}
            >
              Registered Comms. Designer
            </div>
          </div>

          {/* Verified checkmark */}
          <div
            style={{
              display:      'flex',
              alignItems:   'center',
              gap:           4,
              padding:      '2px 7px',
              borderRadius: 99,
              background:   'rgba(52,211,153,0.10)',
              border:       '1px solid rgba(52,211,153,0.30)',
            }}
          >
            <ShieldCheck style={{ width: 9, height: 9, color: 'rgba(52,211,153,0.90)' }} strokeWidth={2.5} />
            <span style={{ fontSize: 8, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(52,211,153,0.85)' }}>
              Verified
            </span>
          </div>
        </div>

        {/* RCDD monogram — bright solid fill for maximum legibility on black */}
        <div
          style={{
            fontSize:      26,
            fontWeight:    900,
            letterSpacing: '0.28em',
            lineHeight:    1,
            marginBottom:  8,
            color:         isElite ? '#FCE38A' : '#8FF7EA',
            textShadow:    isElite
              ? '0 0 12px rgba(252,211,77,0.55), 0 1px 2px rgba(0,0,0,0.6)'
              : '0 0 12px rgba(45,212,191,0.55), 0 1px 2px rgba(0,0,0,0.6)',
          }}
        >
          RCDD
        </div>

        {/* Divider */}
        <div
          style={{
            height:        1,
            marginBottom:  10,
            background:    isElite
              ? 'linear-gradient(90deg, rgba(252,211,77,0.35) 0%, rgba(37,181,168,0.20) 60%, transparent 100%)'
              : 'linear-gradient(90deg, rgba(37,181,168,0.35) 0%, rgba(99,179,237,0.20) 60%, transparent 100%)',
          }}
        />

        {/* Footer row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 8, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(148,172,210,0.40)', marginBottom: 2 }}>
              BICSI ID
            </div>
            <div
              style={{
                fontFamily:    'IBM Plex Mono, monospace',
                fontSize:      13,
                fontWeight:    600,
                letterSpacing: '0.16em',
                color:         isElite ? 'rgba(252,211,77,0.90)' : 'rgba(94,234,212,0.90)',
              }}
            >
              #{id}
            </div>
          </div>

          <div
            style={{
              padding:      '3px 8px',
              borderRadius: 4,
              background:   isElite ? 'rgba(252,211,77,0.10)' : 'rgba(37,181,168,0.10)',
              border:       `1px solid ${isElite ? 'rgba(252,211,77,0.28)' : 'rgba(37,181,168,0.28)'}`,
            }}
          >
            <div style={{ fontSize: 7, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'rgba(148,172,210,0.45)', marginBottom: 1 }}>
              Tier
            </div>
            <div
              style={{
                fontSize:      10,
                fontWeight:    700,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color:         isElite ? 'rgba(252,211,77,0.85)' : 'rgba(94,234,212,0.85)',
              }}
            >
              {tierLabel}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

export function RcddBadge({ bicsiId, tier, variant = 'inline' }: RcddBadgeProps): JSX.Element {
  if (variant === 'card') return <CredentialCard bicsiId={bicsiId} tier={tier} />;
  return <InlinePill bicsiId={bicsiId} tier={tier} />;
}
